import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PrefetchLink } from '@/components/PrefetchLink';
import { buildLoginHref } from '@/lib/auth';
import {
  Phone,
  BookOpen,
  Heart,
  User,
  ShoppingBag,
  Menu,
  Package,
  MapPin,
  LogOut,
  Star,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useCartStore } from '@/store/cartStore';
import { categoryApi, extractResource } from '@/lib/api';
import SearchBar from './SearchBar';
import MobileNav from './MobileNav';

type DrawerCategory = { name: string; slug: string; sub_categories: { name: string; slug: string }[] };

export default function Header() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [categories, setCategories] = useState<DrawerCategory[]>([]);
  const { isAuthenticated, customer, logout } = useAuthStore();
  const { itemCount } = useCartStore();
  const navigate = useNavigate();
  const location = useLocation();
  const loginHref = buildLoginHref(location.pathname, location.search);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lazy-load categories the first time the mobile drawer opens. Keeps the
  // initial page load lean (no categories API on every route) and still gives
  // the drawer a real list as soon as the user hits the hamburger.
  useEffect(() => {
    if (!mobileNavOpen || categories.length) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await categoryApi.getAll();
        const list = extractResource<DrawerCategory[]>(res.data, 'categories') ?? [];
        if (!cancelled) setCategories(list);
      } catch {
        /* silent — drawer just shows an empty categories section */
      }
    })();
    return () => { cancelled = true; };
  }, [mobileNavOpen, categories.length]);

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  return (
    <>
      <header className="site-header">
        <div className="header-inner">
          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-btn"
            aria-label="Open menu"
            onClick={() => setMobileNavOpen(true)}
          >
            <Menu />
          </button>

          {/* Logo */}
          <Link to="/" className="logo">
            Esy<span>Prints</span>
          </Link>

          {/* Search */}
          <SearchBar />

          {/* Header Actions. On mobile these are stripped down — the bottom tab
              bar carries the primary Cart/Account/Home nav and the hamburger
              carries categories. The desktop-only items (phone/projects/favs)
              are suppressed via the `desktop-only` class so the mobile top bar
              stays app-like: just logo + search + cart. */}
          <div className="header-actions">
            {/* Help */}
            <a href="tel:1800XXXXXXX" className="desktop-only">
              <Phone size={22} />
              <span>Help 1800-XXX-XXXX</span>
            </a>

            {/* My Projects */}
            <PrefetchLink
              to="/account/designs"
              className="desktop-only"
              prefetch={() => import('@/pages/account/DesignsPage')}
            >
              <BookOpen size={22} />
              <span>My Projects</span>
            </PrefetchLink>

            {/* My Favourites */}
            <PrefetchLink
              to="/account/wishlist"
              className="desktop-only"
              prefetch={() => import('@/pages/account/WishlistPage')}
            >
              <Heart size={22} />
              <span>My Favourites</span>
            </PrefetchLink>

            {/* Sign In / Account — suppressed on mobile (bottom nav carries it). */}
            <div ref={userMenuRef} className="relative desktop-only">
              {isAuthenticated ? (
                <button onClick={() => setUserMenuOpen(!userMenuOpen)}>
                  <User size={22} />
                  <span>{customer?.name || 'Account'}</span>
                </button>
              ) : (
                <PrefetchLink to={loginHref} prefetch={() => import('@/pages/auth/LoginPage')}>
                  <User size={22} />
                  <span>Sign In</span>
                </PrefetchLink>
              )}

              {/* User dropdown */}
              {userMenuOpen && isAuthenticated && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-[var(--grey-200)] py-2 z-50">
                  <div className="px-4 py-3 border-b border-[var(--grey-200)]">
                    <p className="text-sm font-semibold text-[var(--grey-900)]">
                      {customer?.name || 'Welcome!'}
                    </p>
                    <p className="text-xs text-[var(--grey-500)]">{customer?.phone}</p>
                  </div>
                  <DropdownItem href="/account" icon={<User size={16} />} label="My Profile" onClick={() => setUserMenuOpen(false)} />
                  <DropdownItem href="/orders" icon={<Package size={16} />} label="My Orders" onClick={() => setUserMenuOpen(false)} />
                  <DropdownItem href="/account/designs" icon={<BookOpen size={16} />} label="My Designs" onClick={() => setUserMenuOpen(false)} />
                  <DropdownItem href="/account/addresses" icon={<MapPin size={16} />} label="Addresses" onClick={() => setUserMenuOpen(false)} />
                  <DropdownItem href="/account/loyalty" icon={<Star size={16} />} label="Loyalty Points" onClick={() => setUserMenuOpen(false)} />
                  <div className="border-t border-[var(--grey-200)] mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-[var(--error)] hover:bg-[var(--error-bg)] transition-colors"
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Cart — kept visible on mobile too (top-right cart access is a
                common app pattern even with a bottom tab), but its label is
                hidden on mobile via the existing @media rule. */}
            <PrefetchLink to="/cart" prefetch={() => import('@/pages/cart/CartPage')}>
              <span className="cart-badge">
                <ShoppingBag size={22} />
                {itemCount > 0 && (
                  <span className="count">{itemCount > 99 ? '99+' : itemCount}</span>
                )}
              </span>
              <span>Cart</span>
            </PrefetchLink>
          </div>
        </div>
      </header>

      <MobileNav
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        categories={categories}
      />
    </>
  );
}

function DropdownItem({
  href,
  icon,
  label,
  onClick,
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Link
      to={href}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-2.5 text-sm text-[var(--grey-700)] hover:bg-[var(--grey-100)] transition-colors"
    >
      <span className="text-[var(--grey-500)]">{icon}</span>
      {label}
    </Link>
  );
}
